/**
 * Copyright Highway9 Networks Inc.
 */
import { createAsyncThunk, createEntityAdapter, createSelector, createSlice, type EntityState, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "..";
import { cacertConfigService } from "~/services";
import type { CACertificate } from "~/types/CACertificate";
import { createSocketPatchReducer } from "../events/socketPatch";

export const caCertsAdapter = createEntityAdapter<CACertificate>();

interface CaCertState {
  certsData: EntityState<CACertificate>;
  loading: boolean;
  selectedId: string | null;
  open: boolean;
}

const initialState: CaCertState = {
  certsData: caCertsAdapter.getInitialState(),
  loading: true,
  selectedId: null,
  open: false,
};

export const fetchCACertConfigs = createAsyncThunk(
  "cacertConfig/fetchCACertConfigs",
  async () => {
    try {
      const data = await cacertConfigService.getCACertConfigData();
      return data;
    } catch (error) {
      console.error("Error fetching access config data:", error);
      throw error;
    }
  },
);

const caCertSlice = createSlice({
  name: "caCert",
  initialState,
  reducers: {
    /** Store selection identity only; resolve the entity via selectors. */
    setSelectedConfig: (state, action: PayloadAction<string | null>) => {
      state.selectedId = action.payload;
    },
    setDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.open = action.payload;
    },
    applySocketPatches: createSocketPatchReducer(caCertsAdapter, (s) => s.certsData),
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCACertConfigs.fulfilled, (state, action) => {
        state.loading = false;
        caCertsAdapter.setAll(state.certsData, action.payload);
      })
      // MCS-3868: clear loading on failure so the CA Cert tab can't spin forever on a 403.
      .addCase(fetchCACertConfigs.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const caCertActions = caCertSlice.actions;

const caCertEntitySelectors = caCertsAdapter.getSelectors(
  (state: RootState) => state.caCert.certsData,
);

export const caCertData = caCertEntitySelectors.selectAll;
export const caCertEntities = caCertEntitySelectors.selectEntities;

export const caCertState = createSelector(
  [(state: RootState) => state.caCert.selectedId, caCertEntities],
  (selectedId, entities): CACertificate | null =>
    selectedId ? entities[selectedId] ?? null : null,
);

export const caCertLoading = (state: RootState) => state.caCert.loading;
export const caCertOpen = (state: RootState) => state.caCert.open;

export default caCertSlice.reducer;
